import React, { FC, useCallback, useState } from "react";
import { useLocation } from 'react-router-dom';

import { generateFollowThroughPattern } from "@xapp/arachne-utils";
import {
    Handler,
    App as StentorApp,
    Entity as StentorEntity,
    Handler as StentorHandler,
    Intent as StentorIntent,
    APP_STATUS_INCOMPLETE
} from "stentor-models";

import { INTELLIGENT_SEARCH } from "constants/channels";
import {
    CreateOrgAndAppOnboardingMutationVariables,
    UpdateAppInput,
    useAddChatWidgetChannelMutation,
    useAddFormWidgetChannelMutation,
    useAddLexV2ChannelMutation,
    useAnalyzeWebsiteMutation,
    useCreateOrgAndAppOnboardingMutation,
    UserProfile,
    useStartCrawlMutation,
    useUpdateApplicationMutation,
    useUpdateSearchChannelMutation, // can be used for adding or updated
    useUpdateStatusMutation,
} from "generated/graphql";
import { getApiUrl } from "utils/getApiUrl";
import { wrapUrqlMutation, convertHandler } from "utils/graphql";
import { useBoolState } from "utils/bool";
import { getProducts } from "utils/getProducts";
import { cleanObj } from "utils/Json";
import { ChatWidgetInput, generateCreateAppWidgetInput } from "utils/generateCreateAppWidgetInput";
import { FormWidgetInput, generateFormWidgetInput } from "utils/generateFormWidgetInput";

import { SimpleConfigureAndCreateStep, SimpleConfigureAndCreateStepProps } from "../components/SimpleConfigureAndCreateStep";
import { OnboardingProps } from "../models";
import { buildDefaultSearchTheme } from "components/SearchWidgetThemeBuilder/SearchWidgetThemeBuilder";

function useQueryParams() {
    return new URLSearchParams(useLocation().search);
}

export interface SimpleConfigureAndCreateStepContainerProps extends Omit<SimpleConfigureAndCreateStepProps, "onCreate"> {
    /**
     * Slows everything down.
     */
    debug?: boolean;
    /**
     * Profile of the user
     */
    profile: UserProfile;
    /**
     * Selected product
     */
    product: string;
    /**
     * The user is ready for the next step
     * @param app
     */
    onNextStep(props: OnboardingProps): void;
}

export const SimpleConfigureAndCreateStepContainer: FC<SimpleConfigureAndCreateStepContainerProps> = props => {

    const { onNextStep, product: productId, profile, debug } = props;

    const timeout = debug ? 1000 : 200;

    console.log(`Using timeout of ${timeout}`);

    const queryParams = useQueryParams();

    const [creationStatusMessage, setCreationStatusMessage] = useState<string>();
    const [creating, stopCreating, startCreating] = useBoolState(false);
    const [error, setError] = useState<{ error: string, message: string }>();

    const products = getProducts();
    const product = products.find((product) => product.id === productId);

    /**
     * Define some mutations we will use during creation.
     *
     * We want to suppress the global alerts and errors and instead display them on the page itself
     */
    const createApp = wrapUrqlMutation(useCreateOrgAndAppOnboardingMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean, message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble creating the app for you." });
            return { send: false }
        }
    });

    const updateApplication = wrapUrqlMutation(useUpdateApplicationMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean, message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble updating the contact information on the app for you." });
            return { send: false }
        }
    });

    const createLexV2Channel = wrapUrqlMutation(useAddLexV2ChannelMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean, message?: string } => {
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble creating the NLU channel for you." });
            return { send: false }
        }
    });

    const startCrawl = wrapUrqlMutation(useStartCrawlMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean, message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble starting the website crawl for you." });
            return { send: false }
        }
    });

    const analyzeSite = wrapUrqlMutation(useAnalyzeWebsiteMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean, message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble starting the website crawl for you." });
            return { send: false }
        }
    });

    const updateStatus = wrapUrqlMutation(useUpdateStatusMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean, message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble updating the status for you." });
            return { send: false }
        }
    });

    const createWidget = wrapUrqlMutation(useAddChatWidgetChannelMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean, message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble creating the widget channel for you." });
            return { send: false }
        }
    });

    const createSearch = wrapUrqlMutation(useUpdateSearchChannelMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean, message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble creating a search channel for you." });
            return { send: false }
        }
    });

    const createForm = wrapUrqlMutation(useAddFormWidgetChannelMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean, message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble creating the scheduler channel for you." });
            return { send: false }
        }
    });

    /**
     * The main method fo app creation, it all of the mutations.
     *
     * We will want to refactor this to make it more potable and reusable.
     */
    const handleCreate = useCallback(async (createProps: OnboardingProps) => {
        startCreating();

        createProps = cleanObj(createProps);

        // Update the current fields in case of an error
        sessionStorage.setItem("onboarding-appProps", JSON.stringify(createProps));

        // load the template JSON
        const templateName = createProps.template?.name || "lead capture...";
        setCreationStatusMessage(`Preparing data for ${templateName}...`);

        // Old way...
        // const templateUrl = createProps?.template?.url || "https://templates.xapp.ai/contact-capture.json";
        const templateId = createProps?.template?.id || "contact-capture";
        const templateUrl = `/templates/${templateId}.json`;

        type Template = { app: StentorApp, entities: StentorEntity[], intents: StentorIntent[], handlers: StentorHandler[] };

        const template: Template = await fetch(templateUrl)
            .then((response) => {
                return response.json();
            }).catch((e) => {
                setError({ message: `Could not load the template: ${templateId}`, error: e.message });
                stopCreating();
            });
        if (!template) {
            // bail
            return;
        }
        setCreationStatusMessage(`Template ${template.app.name} loaded...`)
        /**
         * No model for our templates so we clean off utterances & don't pass intents & entities
         */
        const cleanHandler = (handler: Handler): Handler => {
            // delete slots
            const cleaned = { ...handler };
            cleaned.slots = [];
            cleaned.utterancePatterns = [];

            return cleaned;
        }

        const handlers = template.handlers.map(cleanHandler).map(convertHandler);

        const variables: CreateOrgAndAppOnboardingMutationVariables = {
            app: {
                app: {
                    name: createProps.name,
                    // businessDescription: createProps.businessDescription || undefined,
                    website: createProps.website || undefined,
                    templateType: templateId,
                    //   icon: createProps.logo
                    // TODO: Ability to add leadsContact & leadsPhoneContact & website
                },
                // they are empty so we don't create a model
                entities: [],
                intents: [],
                handlers
            },
            org: {
                name: createProps.name,
                //  logoUrl: createProps.logo
            }
        };

        setCreationStatusMessage("Creating AI agent...");

        const result = await createApp(variables);

        const app = result?.data?.org?.add?.app;

        if (!app) {
            stopCreating();
            return;
        }

        const { appId, organizationId } = app;
        createProps.appId = appId;
        createProps.organizationId = organizationId;

        const updateAppInput: UpdateAppInput = {}

        const partner: string | undefined = queryParams.get("partner") || product?.metadata?.["partner"];

        if (partner) {
            updateAppInput.refernceId = {
                service: partner,
                // we don't know this yet
                id: "unknown"
            }
        }

        updateAppInput.beta = createProps.beta;

        await new Promise(r => setTimeout(r, timeout));

        await updateApplication({
            appId,
            app: updateAppInput
        });

        if (app.website) {
            // Start Crawl
            setCreationStatusMessage("Starting website ingestion...");
            const pattern: string[] = [generateFollowThroughPattern(app.website)];
            await startCrawl({
                appId,
                url: app.website,
                pattern,
                channelId: ""
                //    channelId:  widget.id
            });

            const result = await analyzeSite({ url: createProps.website }).catch((e) => {
                console.error("Failed to analyze site", e);
            });

            if (result) {
                createProps.executionId = result?.data?.onboarding?.startRetrieveWebsiteDetails?.executionId;
            }
        }

        // Create Chat Widget & Search Bar (& GBM);
        setCreationStatusMessage("Configuring channels...");
        await new Promise(r => setTimeout(r, timeout));

        const chatName = createProps.name ? `${createProps.name}` : "Widget";

        // START WIDGET CREATE
        const chatWidgetProps: ChatWidgetInput = {
            ...createProps,
            name: chatName,
            primaryColor: "#1AA7DC",
            appId,
            templateId: createProps.template.id
        }

        const chatWidgetInput = generateCreateAppWidgetInput(chatWidgetProps);

        // if not free tier, delete the branding
        if (product) {
            delete chatWidgetInput.footer;
        }

        const createWidgetResponse = await createWidget({ appId, channel: chatWidgetInput });

        const widget = createWidgetResponse?.data?.addChatWidgetChannel;
        createProps.chatWidgetId = widget?.id;

        if (!widget) {
            setError({ message: `Could not create widget.`, error: `Widget was undefined` });
            stopCreating();
            return;
        }

        const apiUrl = getApiUrl();

        // We are just going to make both channels and prevent access to them
        // based on current plan
        // const supportSearch: boolean = product?.metadata?.search === "true";
        // const supportGBM: boolean = product?.metadata?.gbm === "true";

        // create searchChannel
        await new Promise(r => setTimeout(r, timeout));
        await createSearch({
            appId,
            channel: {
                type: INTELLIGENT_SEARCH,
                name: "AI Search",
                theme: buildDefaultSearchTheme("#1AA7DC"),
                connection: {
                    serverUrl: `${apiUrl}/apps/${appId}/run`
                },
                autocompleteSuggestionsUrl: `${apiUrl}/chat/suggestions?key=$CHANNEL_KEY&type=FAQ`,
            }
        });

        const formName = createProps.name ? `${createProps.name} Booking` : "Booking";

        // Create Schedular!
        const appFormInput: FormWidgetInput = {
            ...createProps,
            name: formName,
            appId,
            primaryColor: "#1AA7DC",
        };

        if (createProps.place && createProps.place.formattedAddress) {
            appFormInput.address = {
                streetAddress: createProps.place?.formattedAddress
            }
        }

        const schedularInput = generateFormWidgetInput(appFormInput);

        await createForm({
            appId,
            channel: schedularInput
        });

        setCreationStatusMessage("Setting business type...");
        await new Promise(r => setTimeout(r, timeout));
        // LEX v2
        await createLexV2Channel({
            appId,
            channel: {
                type: "lex-v2",
                botName: templateId
            }
        });

        await updateStatus({
            appId,
            type: APP_STATUS_INCOMPLETE,
            notes: `Created ${new Date().toDateString()} by ${profile.email} during onboarding.`
        });

        // Done
        setCreationStatusMessage(`Great start! Preparing next step...`);
        await new Promise(r => setTimeout(r, timeout));

        stopCreating();

        // Move on!
        onNextStep(createProps);
    }, [analyzeSite, createApp, createForm, createLexV2Channel, createSearch, createWidget, onNextStep, product, profile.email, queryParams, startCrawl, startCreating, stopCreating, timeout, updateApplication, updateStatus]);

    return (
        <SimpleConfigureAndCreateStep
            {...props}
            creating={creating}
            creatingStatusMessage={creationStatusMessage}
            onCreate={handleCreate}
            error={error}
        />
    );
}